import { useState, useRef, useEffect, useCallback } from 'react'
import type { DashboardLayoutPreset } from '../../stores/dashboardLayoutStore'

interface LayoutPresetsMenuProps {
    availableLayouts: DashboardLayoutPreset[]
    currentLayoutId: string | null
    currentLayoutSource: string
    isEditing: boolean
    hasUnsavedChanges: boolean
    dashboardType: 'daily' | 'monthly' | 'custom-range' | 'inventory'
    onSelectLayout: (id: string) => void
    onExport: () => string
    onImport: (json: string) => string | null  // returns error string or null
    onSaveFirst: () => void                     // opens the save dialog
}

export default function LayoutPresetsMenu({
    availableLayouts, currentLayoutId, currentLayoutSource, isEditing,
    hasUnsavedChanges, dashboardType, onSelectLayout, onExport, onImport, onSaveFirst
}: LayoutPresetsMenuProps) {
    const [open, setOpen] = useState(false)
    const [modalMode, setModalMode] = useState<'import' | 'export' | null>(null)
    const [modalJson, setModalJson] = useState('')
    const [importError, setImportError] = useState<string | null>(null)
    const [importSuccess, setImportSuccess] = useState(false)
    const [copied, setCopied] = useState(false)
    const [pendingAction, setPendingAction] = useState<{ type: 'switch'; layoutId: string } | { type: 'import'; json: string } | null>(null)
    const [unsavedPrompt, setUnsavedPrompt] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    // Close popover on outside click
    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const isSystemDefault = !currentLayoutId && currentLayoutSource === 'system'

    // ── Layout selection with unsaved-changes guard ──────────────────
    const handleSelectLayout = useCallback((layoutId: string) => {
        setOpen(false)
        if (hasUnsavedChanges && isEditing) {
            setPendingAction({ type: 'switch', layoutId })
            setUnsavedPrompt(true)
        } else {
            onSelectLayout(layoutId)
        }
    }, [hasUnsavedChanges, isEditing, onSelectLayout])

    // ── Unsaved changes prompt actions ───────────────────────────────
    const handleUnsavedSave = () => {
        setUnsavedPrompt(false)
        // Store the pending action, open save dialog — caller is responsible for
        // continuing the flow once save completes (via onSaveFirst callback)
        onSaveFirst()
        // After save, the pending action stays so the layout switches post-save
        // For simplicity, we clear it — user can re-select after saving
        setPendingAction(null)
    }

    const handleUnsavedDiscard = () => {
        setUnsavedPrompt(false)
        if (pendingAction?.type === 'switch') {
            onSelectLayout(pendingAction.layoutId)
        } else if (pendingAction?.type === 'import') {
            const err = onImport(pendingAction.json)
            if (err) {
                setImportError(err)
                setModalMode('import')
                setModalJson(pendingAction.json)
            }
        }
        setPendingAction(null)
    }

    const handleUnsavedCancel = () => {
        setUnsavedPrompt(false)
        setPendingAction(null)
    }

    // ── Export ───────────────────────────────────────────────────────
    const handleOpenExport = () => {
        setOpen(false)
        const json = onExport()
        setModalJson(json)
        setModalMode('export')
        setImportError(null)
        setImportSuccess(false)
        setCopied(false)
    }

    const handleCopyJson = async () => {
        try {
            await navigator.clipboard.writeText(modalJson)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Fallback: select text
            const textarea = document.querySelector('.layout-ie-textarea') as HTMLTextAreaElement
            if (textarea) { textarea.select(); document.execCommand('copy') }
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    // ── Import ──────────────────────────────────────────────────────
    const handleOpenImport = () => {
        setOpen(false)
        setModalJson('')
        setModalMode('import')
        setImportError(null)
        setImportSuccess(false)
    }

    const handleImportSubmit = () => {
        setImportError(null)
        setImportSuccess(false)

        if (!modalJson.trim()) {
            setImportError('Please paste a layout JSON string.')
            return
        }

        if (hasUnsavedChanges && isEditing) {
            setPendingAction({ type: 'import', json: modalJson })
            setUnsavedPrompt(true)
            setModalMode(null)
            return
        }

        const err = onImport(modalJson)
        if (err) {
            setImportError(err)
        } else {
            setImportSuccess(true)
            setTimeout(() => setModalMode(null), 800)
        }
    }

    // ── Render ───────────────────────────────────────────────────────
    return (
        <>
            {/* Presets Button */}
            <div ref={menuRef} style={{ position: 'relative' }}>
                <button className="edit-mode-btn" onClick={() => setOpen(o => !o)}>
                    📋 Layout Presets
                </button>

                {/* Popover */}
                {open && (
                    <div className="layout-presets-popover">
                        {/* System Default — always first */}
                        <button
                            className={`layout-presets-item${isSystemDefault ? ' active' : ''}`}
                            onClick={() => handleSelectLayout('system-default')}
                        >
                            <span className="presets-check">{isSystemDefault ? '✓' : ''}</span>
                            <span className="presets-label">System Default</span>
                        </button>

                        {/* Saved Layouts */}
                        {availableLayouts.map(l => {
                            const isActive = currentLayoutId === l.id
                            return (
                                <button
                                    key={l.id}
                                    className={`layout-presets-item${isActive ? ' active' : ''}`}
                                    onClick={() => handleSelectLayout(l.id)}
                                >
                                    <span className="presets-check">{isActive ? '✓' : ''}</span>
                                    <span className="presets-label">
                                        {l.name || 'Untitled'}
                                        {l.isDefault && <span className="presets-badge">Default</span>}
                                    </span>
                                </button>
                            )
                        })}

                        {/* Divider */}
                        <div className="layout-presets-divider" />

                        {/* Import / Export */}
                        <button className="layout-presets-item" onClick={handleOpenImport}>
                            <span className="presets-check" style={{ fontSize: '13px' }}>📥</span>
                            <span className="presets-label">Import Layout…</span>
                        </button>
                        <button className="layout-presets-item" onClick={handleOpenExport}>
                            <span className="presets-check" style={{ fontSize: '13px' }}>📤</span>
                            <span className="presets-label">Export Layout</span>
                        </button>
                    </div>
                )}
            </div>

            {/* ── Import / Export Modal ──────────────────────────────── */}
            {modalMode && (
                <div className="layout-ie-overlay" onClick={() => setModalMode(null)}>
                    <div className="layout-ie-modal" onClick={e => e.stopPropagation()}>
                        <h3>{modalMode === 'export' ? 'Export Layout' : 'Import Layout'}</h3>

                        {modalMode === 'export' && (
                            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', margin: '0 0 var(--space-3)' }}>
                                Copy this JSON to save or share your current {dashboardType} dashboard layout.
                            </p>
                        )}

                        {modalMode === 'import' && (
                            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', margin: '0 0 var(--space-3)' }}>
                                Paste a previously exported {dashboardType} dashboard layout JSON below.
                            </p>
                        )}

                        <textarea
                            className="layout-ie-textarea"
                            value={modalJson}
                            onChange={e => { setModalJson(e.target.value); setImportError(null); setImportSuccess(false) }}
                            readOnly={modalMode === 'export'}
                            placeholder={modalMode === 'import' ? 'Paste layout JSON here…' : ''}
                            spellCheck={false}
                            rows={12}
                            autoFocus={modalMode === 'import'}
                        />

                        {importError && (
                            <div className="layout-ie-error">{importError}</div>
                        )}

                        {importSuccess && (
                            <div className="layout-ie-success">✓ Layout imported successfully!</div>
                        )}

                        <div className="layout-ie-actions">
                            {modalMode === 'export' ? (
                                <>
                                    <button className="btn btn-primary" onClick={handleCopyJson}>
                                        {copied ? '✓ Copied!' : 'Copy to Clipboard'}
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => setModalMode(null)}>Close</button>
                                </>
                            ) : (
                                <>
                                    <button className="btn btn-primary" onClick={handleImportSubmit} disabled={!modalJson.trim()}>
                                        Import
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => setModalMode(null)}>Cancel</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Unsaved Changes Prompt ─────────────────────────────── */}
            {unsavedPrompt && (
                <div className="layout-ie-overlay" onClick={handleUnsavedCancel}>
                    <div className="layout-ie-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '380px' }}>
                        <h3>Unsaved Changes</h3>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 var(--space-5)' }}>
                            You have unsaved layout changes. Would you like to save them first?
                        </p>
                        <div className="layout-ie-actions">
                            <button className="btn btn-primary" onClick={handleUnsavedSave}>
                                💾 Save
                            </button>
                            <button className="btn btn-secondary" onClick={handleUnsavedDiscard}
                                style={{ borderColor: 'var(--color-warning)', color: 'var(--color-warning)' }}>
                                Discard
                            </button>
                            <button className="btn btn-secondary" onClick={handleUnsavedCancel}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
